import React from "react";
import { Table, Button, Modal, message, Input } from "antd";
import {
  PlayCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  RobotOutlined,
  ThunderboltOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  BenchmarkConfig,
  fetchBenchmarkConfigs,
  deleteBenchmarkConfig,
  triggerBenchmarkRun,
  fetchBenchmarkRunCountForConfig,
  RuleAction,
} from "@/lib/api/resources/benchmark";
import { Agent, AiProfile } from "@/lib/api/resources/agents";
import { useAffiliate } from "@/lib/hooks/useAffiliate";
import { RunSettingsModal } from "./RunSettingsModal";
import { CopyOutlined } from "@ant-design/icons";
import { createBenchmarkConfig } from "@/lib/api/resources/benchmark";

const DeleteConfigButton = ({
  record,
  onDelete,
}: {
  record: BenchmarkConfig;
  onDelete: (id: string) => void;
}) => {
  const [loading, setLoading] = React.useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setLoading(true);
    let count = 0;
    try {
      count = await fetchBenchmarkRunCountForConfig(record.id);
    } catch (error) {
      console.error("Failed to fetch run count", error);
    } finally {
      setLoading(false);
    }

    Modal.confirm({
      title: "Delete Benchmark Configuration",
      icon: <ExclamationCircleFilled className="text-red-500" />,
      width: 500,
      content: (
        <div className="mt-4 space-y-3">
          <p className="text-gray-600">
            Are you sure you want to delete the configuration{" "}
            <strong className="text-gray-900">"{record.name}"</strong>?
          </p>
          <div className="bg-red-50 border border-red-100 p-4 rounded-xl shadow-sm">
            <h4 className="text-red-800 font-semibold mb-2 text-sm flex items-center gap-2">
              <ExclamationCircleFilled /> Impact Analysis
            </h4>
            <ul className="list-disc pl-5 text-red-700 text-sm space-y-1">
              <li>This configuration template will be permanently removed.</li>
              <li>
                You will no longer be able to trigger new runs using this setup.
              </li>
              <li>
                <strong className="font-semibold">
                  Historical Runs Preserved:
                </strong>{" "}
                {count > 0
                  ? `The ${count} Benchmark Run${count === 1 ? "" : "s"} previously generated will remain safely stored.`
                  : `Any Benchmark Runs previously generated will remain safely stored.`}
              </li>
            </ul>
          </div>
        </div>
      ),
      okText: "Delete Configuration",
      okType: "danger",
      cancelText: "Cancel",
      okButtonProps: {
        className:
          "bg-red-600 hover:bg-red-700 text-white border-none shadow-md shadow-red-500/20",
      },
      onOk: () => onDelete(record.id),
    });
  };

  return (
    <Button
      type="text"
      danger
      icon={<DeleteOutlined />}
      loading={loading}
      onClick={handleClick}
      className="hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-600"
    />
  );
};

interface BenchmarkConfigsTableProps {
  agents?: Agent[];
  ruleActions?: RuleAction[];
  aiProfiles?: AiProfile[];
  onEdit: (config: BenchmarkConfig) => void;
  onRunStarted?: (runId: string) => void;
}

export const BenchmarkConfigsTable: React.FC<BenchmarkConfigsTableProps> = ({
  agents = [],
  ruleActions = [],
  aiProfiles = [],
  onEdit,
  onRunStarted,
}) => {
  const { currentAffiliate } = useAffiliate();
  const queryClient = useQueryClient();

  const bareUuid = (idOrIri: string): string =>
    String(idOrIri).split("/").pop() ?? "";

  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [sortField, setSortField] = React.useState("createdAt");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");
  const [searchTerm, setSearchTerm] = React.useState("");

  const { data, isLoading } = useQuery({
    queryKey: [
      "benchmarkConfigs",
      currentAffiliate,
      currentPage,
      sortField,
      sortOrder,
      searchTerm,
    ],
    queryFn: () =>
      fetchBenchmarkConfigs(currentPage, sortField, sortOrder, searchTerm),
    enabled: !!currentAffiliate,
  });

  const [runModalOpen, setRunModalOpen] = React.useState(false);
  const [selectedRunConfig, setSelectedRunConfig] =
    React.useState<BenchmarkConfig | null>(null);

  const [copyModalOpen, setCopyModalOpen] = React.useState(false);
  const [selectedCopyConfig, setSelectedCopyConfig] =
    React.useState<BenchmarkConfig | null>(null);
  const [copyName, setCopyName] = React.useState("");
  const [copyError, setCopyError] = React.useState("");
  const [isCheckingUnique, setIsCheckingUnique] = React.useState(false);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteBenchmarkConfig(id),
    onSuccess: () => {
      message.success("Benchmark configuration deleted");
      queryClient.invalidateQueries({ queryKey: ["benchmarkConfigs"] });
    },
    onError: () => {
      message.error("Failed to delete benchmark configuration");
    },
  });

  const copyMutation = useMutation({
    mutationFn: async (newName: string) => {
      if (!selectedCopyConfig) throw new Error("No config selected");

      const agentRef = selectedCopyConfig.agent;
      const agentVal =
        typeof agentRef === "object" && agentRef !== null
          ? (agentRef as any).id || (agentRef as any)["@id"]
          : (agentRef as unknown as string);

      const payloadConfig = { ...selectedCopyConfig.config };

      return createBenchmarkConfig({
        name: newName,
        agent: bareUuid(agentVal),
        ruleAction: selectedCopyConfig.ruleActionId,
        config: payloadConfig,
      });
    },
    onSuccess: () => {
      message.success("Configuration copied successfully");
      queryClient.invalidateQueries({ queryKey: ["benchmarkConfigs"] });
      setCopyModalOpen(false);
    },
    onError: (error: any) => {
      message.error(
        error?.response?.data?.detail || "Failed to copy configuration",
      );
    },
  });

  const handleCopyOk = async () => {
    if (!copyName.trim()) {
      setCopyError("Name is required");
      return;
    }

    setIsCheckingUnique(true);
    try {
      const result = await fetchBenchmarkConfigs(
        1,
        "createdAt",
        "desc",
        copyName.trim(),
      );
      const items = (result as any)?.["hydra:member"] || result || [];
      const exactMatch = items.find(
        (c: any) => c.name.toLowerCase() === copyName.trim().toLowerCase(),
      );

      if (exactMatch) {
        setCopyError("A configuration with this name already exists");
        setIsCheckingUnique(false);
        return;
      }

      copyMutation.mutate(copyName.trim());
    } catch (err: any) {
      console.error(err);
      setCopyError("Failed to check name uniqueness");
    } finally {
      setIsCheckingUnique(false);
    }
  };

  const handleOpenCopy = (record: BenchmarkConfig) => {
    setSelectedCopyConfig(record);
    setCopyName(`${record.name} (Copy)`);
    setCopyError("");
    setCopyModalOpen(true);
  };

  const runMutation = useMutation({
    mutationFn: ({
      config,
      overrideLimit,
    }: {
      config: BenchmarkConfig;
      overrideLimit: number | null;
    }) => {
      const payloadConfig = { ...config.config };
      if (overrideLimit !== null) {
        payloadConfig.limit = overrideLimit;
      }

      return triggerBenchmarkRun(
        bareUuid(
          typeof config.agent === "string" ? config.agent : config.agent.id,
        ),
        config.ruleActionId || undefined,
        payloadConfig,
        String(config.id).split("/").pop(),
      );
    },
    onSuccess: (run) => {
      message.success("Benchmark run started!");
      queryClient.invalidateQueries({ queryKey: ["benchmarkRuns"] });
      setRunModalOpen(false);

      const rawId = run?.id || (run as any)?.["@id"];
      const finalId =
        typeof rawId === "string" && rawId.includes("/")
          ? rawId.split("/").pop()
          : rawId;

      if (finalId && onRunStarted) {
        onRunStarted(finalId);
      }
    },
    onError: () => {
      message.error("Failed to start benchmark run");
    },
  });

  const handleTableChange = (pagination: any, _filters: any, sorter: any) => {
    setCurrentPage(pagination.current || 1);
    setPageSize(pagination.pageSize || 10);
    if (sorter && sorter.columnKey) {
      setSortField(sorter.columnKey);
      setSortOrder(sorter.order === "ascend" ? "asc" : "desc");
    }
  };

  const totalItems =
    (data as any)?.["hydra:totalItems"] ||
    ((data as any)?.["hydra:member"] || data || []).length;

  const columns = [
    {
      title: "Configuration Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      width: 250,
      render: (text: string) => (
        <div className="flex items-center gap-3 py-2">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-tr from-indigo-500 to-purple-500 flex items-center justify-center shadow-md shadow-indigo-500/20 text-white shrink-0">
            <SettingOutlined className="text-lg opacity-90" />
          </div>
          <span
            className="font-semibold text-gray-800 text-sm tracking-tight line-clamp-2"
            title={text}
          >
            {text}
          </span>
        </div>
      ),
    },
    {
      title: "Agent & Target",
      key: "agent",
      width: 250,
      render: (_: any, record: BenchmarkConfig) => {
        const agentId =
          typeof record.agent === "string"
            ? record.agent
            : record.agent?.id || (record.agent as any)?.["@id"];
        const matchedAgent = agents.find(
          (a: any) =>
            (a["@id"] || a.id)?.toLowerCase() === agentId?.toLowerCase() ||
            agentId?.toLowerCase()?.includes(a.id?.toLowerCase()),
        );
        const agentName = matchedAgent
          ? matchedAgent.name
          : typeof record.agent === "object" && (record.agent as any)?.name
            ? (record.agent as any).name
            : agentId || "Unknown";

        const raId = record.ruleActionId;
        const matchedRa = raId
          ? ruleActions.find(
              (ra: any) =>
                (ra["@id"] || ra.id)?.toLowerCase() === raId?.toLowerCase() ||
                raId?.toLowerCase()?.includes(ra.id?.toLowerCase()),
            )
          : null;

        const agentConnection = matchedAgent?.aiConnection;
        let modelLabel = agentConnection?.model;
        let providerLabel =
          agentConnection?.providerLabel || agentConnection?.provider;

        const p = record.config?.parameters;
        if (p?.aiProfileId) {
          const profileId =
            typeof p.aiProfileId === "string"
              ? p.aiProfileId.split("/").pop()
              : p.aiProfileId;
          const profile = aiProfiles.find(
            (prof: any) =>
              prof.id === profileId || prof["@id"]?.endsWith(profileId),
          );
          if (profile?.aiConnection) {
            // The API key is no longer returned on read; derive the model/provider
            // from the read-safe aiConnection decomposition instead.
            modelLabel = profile.aiConnection.model || modelLabel;
            providerLabel =
              profile.aiConnection.providerLabel ||
              profile.aiConnection.provider ||
              providerLabel;
          }
        }

        if (providerLabel && providerLabel === providerLabel.toLowerCase()) {
          providerLabel =
            providerLabel.charAt(0).toUpperCase() + providerLabel.slice(1);
        }

        const subtext = modelLabel
          ? `${providerLabel ? `${providerLabel} · ` : ""}${modelLabel}`
          : null;

        return (
          <div className="flex flex-col items-start gap-1.5 py-2 max-w-full">
            <div className="flex flex-col items-start gap-0.5 max-w-full">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50/80 border border-blue-100 rounded-lg text-blue-700 text-sm font-medium shadow-sm max-w-full">
                <RobotOutlined className="text-blue-500 shrink-0" />
                <span className="truncate" title={agentName}>
                  {agentName}
                </span>
              </div>
              {subtext && (
                <div
                  className="text-[11px] leading-tight text-gray-500 truncate px-1 max-w-full"
                  title={subtext}
                >
                  {subtext}
                </div>
              )}
            </div>
            {raId && (
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-50/80 border border-purple-100 rounded-lg text-purple-700 text-xs font-medium shadow-sm max-w-full">
                <ThunderboltOutlined className="text-purple-500 shrink-0" />
                <span
                  className="truncate"
                  title={matchedRa ? matchedRa.label || matchedRa.name : raId}
                >
                  {matchedRa ? matchedRa.label || matchedRa.name : raId}
                </span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      width: 150,
      render: (text: string) => {
        const date = new Date(text);
        return (
          <div className="flex flex-col py-2">
            <span className="text-sm font-medium text-gray-700 whitespace-nowrap">
              {date.toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
            <span className="text-xs text-gray-400 font-medium whitespace-nowrap">
              {date.toLocaleTimeString(undefined, {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </div>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      align: "right" as const,
      fixed: "right" as const,
      width: 180,
      className: "bg-white z-10",
      render: (_: any, record: BenchmarkConfig) => (
        <div className="flex items-center justify-end gap-1 opacity-100 lg:opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200 bg-white">
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedRunConfig(record);
              setRunModalOpen(true);
            }}
            className="bg-orange-500 hover:bg-orange-600 border-none shadow-md shadow-orange-500/20 rounded-lg font-medium h-auto py-1 px-3 text-xs"
          >
            Run
          </Button>
          <Button
            type="text"
            icon={<CopyOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              handleOpenCopy(record);
            }}
            className="text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg ml-1 h-auto py-1 px-2 text-xs"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(record);
            }}
            className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg ml-1 h-auto py-1 px-2 text-xs"
          />
          <DeleteConfigButton
            record={record}
            onDelete={(id) => deleteMutation.mutate(id)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="flex justify-between items-center w-full px-4">
        <Input.Search
          placeholder="Search configurations..."
          allowClear
          onSearch={(value) => {
            setSearchTerm(value);
            setCurrentPage(1);
          }}
          style={{ width: 300 }}
        />
      </div>
      <Table
        columns={columns}
        dataSource={(data as any)?.["hydra:member"] || data || []}
        rowKey="id"
        loading={isLoading}
        scroll={{ x: "max-content" }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalItems,
          showSizeChanger: true,
          className: "px-4 pb-4 mb-0",
        }}
        onChange={handleTableChange}
        onRow={(record: BenchmarkConfig) => ({
          onClick: () => onEdit(record),
          className:
            "group hover:bg-slate-50/50 transition-colors duration-200 cursor-pointer",
        })}
      />

      <RunSettingsModal
        open={runModalOpen}
        config={selectedRunConfig}
        onClose={() => setRunModalOpen(false)}
        onRun={(config, overrideLimit) =>
          runMutation.mutate({ config, overrideLimit })
        }
        isPending={runMutation.isPending}
      />

      <Modal
        title="Copy Configuration"
        open={copyModalOpen}
        onCancel={() => setCopyModalOpen(false)}
        onOk={handleCopyOk}
        okText="Copy"
        confirmLoading={isCheckingUnique || copyMutation.isPending}
        destroyOnClose
      >
        <div className="py-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            New Configuration Name
          </label>
          <Input
            value={copyName}
            onChange={(e) => {
              setCopyName(e.target.value);
              setCopyError("");
            }}
            placeholder="Enter new name"
            onPressEnter={handleCopyOk}
            status={copyError ? "error" : ""}
          />
          {copyError && (
            <div className="text-red-500 text-sm mt-1">{copyError}</div>
          )}
        </div>
      </Modal>
    </div>
  );
};
